import React, { useState, useEffect, useContext } from "react";
import { Card } from "react-bootstrap";
import { Plus } from "react-feather";

import { UserSettingsContext } from "../contexts/UserSettingsContext";
import authenticatedFetch from "../utils/fetch"

import TaskModal from "./TaskModal";
import { TaskCard, TaskSummaryRow, linkButtonStyle, SortButton } from "./TaskCardParts";

const formatDue = (due) => {
  if (!due) return null;
  const parts = due.split("-");
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  const diffInDays = (date - new Date()) / (1000 * 60 * 60 * 24);
  // Same thresholds as the Task card: red once a full day overdue, amber up to 4 days out
  const color = diffInDays <= -1 ? "#C2453D" : (diffInDays <= 4 ? "#B7791F" : "#6B7274");
  return { text: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), color, emphasized: diffInDays <= 4 };
};

const OpenTaskRow = ({ task, entityType }) => {
  const due = formatDue(task.due);
  return (
    <TaskSummaryRow
      task={task}
      entityType={entityType}
      metric={due && <span style={{ fontSize: "12.5px", color: due.color, fontWeight: due.emphasized ? 500 : 400 }}>{due.text}</span>}
    />
  );
};

const INITIAL_VISIBLE = 25;

// practiceIds (provider page) lists the provider's practices; the new task form only offers those, preselecting a sole one
const OpenTasks = ({entityType, entityId, practiceIds}) => {
    const { isH3Admin, authorities, tasksRefresh, setTasksRefresh } = useContext(UserSettingsContext);
    const canEdit = isH3Admin || authorities.includes("TASK_EDIT");
    const [showCreate, setShowCreate] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [dueAsc, setDueAsc] = useState(true);
    const [error, setError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
  
    const refreshData = () => {
      if (!entityType || !entityId) return;

      authenticatedFetch({
        path: "task/open/" + entityType + "/" + entityId,
        successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function() {
          setIsLoaded(true);
          setError(true);
        }
      });
    };

    useEffect(() => {
      refreshData();
    }, [entityType, entityId, tasksRefresh]);

  
    if (!isLoaded)
    {
      return (<Card><Card.Body>Loading...</Card.Body></Card>)
    }
    else if (error || !data)
    {
      return (<Card><Card.Body>Something went wrong... please reload the page...</Card.Body></Card>)
    }
    else
    {
      if (data.length === 0 && !canEdit)
      {
        return <></>
      }

      // Tasks without a due date always sort last; ties (including no due date) fall back to id ascending
      const sorted = [...data].sort((a, b) => {
        let byDue = 0;
        if (a.due && b.due) byDue = dueAsc ? a.due.localeCompare(b.due) : b.due.localeCompare(a.due);
        else if (!a.due && b.due) byDue = 1;
        else if (a.due && !b.due) byDue = -1;
        return byDue !== 0 ? byDue : a.id - b.id;
      });

      return (
        <>
        <TaskCard
          collapsible
          title="Open tasks"
          count={data.length}
          headerRight={
            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
              {data.length > 1 &&
                <SortButton label="" ascending={dueAsc} onToggle={() => setDueAsc(!dueAsc)} ascTitle="Soonest first" descTitle="Latest first" />
              }
              {canEdit &&
                <button type="button" title="New task" aria-label="New task" onClick={() => setShowCreate(true)}
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{ width: "22px", height: "22px", padding: 0, borderRadius: "4px", border: 0, background: "#3E8A80", color: "#fff" }}>
                  <Plus size={15} strokeWidth={2.5} />
                </button>
              }
            </div>
          }
          footer={data.length > INITIAL_VISIBLE &&
            <button type="button" className="btn btn-link p-0" style={linkButtonStyle} onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer" : "View all tasks"}
            </button>
          }
        >
          {sorted.slice(0, showAll ? undefined : INITIAL_VISIBLE).map((task) => <OpenTaskRow key={task.id} task={task} entityType={entityType}/>)}
        </TaskCard>
        {canEdit && showCreate &&
          <TaskModal
            show={showCreate}
            practiceId={entityType === "practice" ? Number(entityId) : (practiceIds && practiceIds.length === 1 ? practiceIds[0] : undefined)}
            allowedProviderId={entityType === "provider" ? Number(entityId) : undefined}
            allowedPracticeIds={entityType === "practice" ? [Number(entityId)] : practiceIds}
            providerId={entityType === "provider" ? Number(entityId) : undefined}
            onCancel={() => setShowCreate(false)}
            onSave={() => { setShowCreate(false); setTasksRefresh(tasksRefresh + 1); }}
          />
        }
        </>
      )
    }
}

export default OpenTasks;
